import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, siteInfo } from '@/mocks/appData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const categoryLabel = currentCategory?.name || '全部';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `CPP ${categoryLabel} - 文章中心`,
    description: `浏览CPP官方的${categoryLabel}相关文章，获取App使用教程、版本更新、安全指南等实用信息。`,
    url: `https://cpp-official.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://cpp-official.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <ScrollToTop />
      <main>
        <Navbar />
        <div className="pt-24 pb-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '文章中心' },
              ]}
            />

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              文章中心
            </h1>
            <p className="text-sm text-foreground-600 mb-8">
              浏览CPP App使用教程、版本更新、安全指南等实用内容
            </p>

            <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium whitespace-nowrap rounded-full transition-all duration-200 ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.cover}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium whitespace-nowrap rounded-full bg-background-50/90 text-foreground-700 backdrop-blur-sm">
                        {categories.find((c) => c.id === article.category)?.name || article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm leading-snug group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>{article.readTime} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}